import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  TextInputField, 
  TextareaField,
  NumberInputField,
  FormSubmitButton,
  SelectField,
} from '@/components/forms/FormComponents'
import { createCategorySchema, updateCategorySchema, type CreateCategoryData, type UpdateCategoryData } from '@/lib/form-schemas'
import { toastHelpers } from '@/lib/toast-helpers'
import { useEffect, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import { resizeImageToDataUrl } from '@/lib/imageUpload'
import { CategoryModifierSection, PriceImpactDialog } from './CategoryModifierSection'
import type { ModifierCategoryPriceImpact } from '@/types'
import apiClient from '@/api/client'
import type { Category } from '@/types'
import { X, Check } from 'lucide-react'
import { CategoryHoursSection } from '@/components/admin/deals/CategoryHoursSection'
import type { KitchenStation } from '@/types'

const PRESET_COLORS = [
  { name: 'Red', value: '#FF6B6B' },
  { name: 'Orange', value: '#FF9F43' },
  { name: 'Yellow', value: '#FECA57' },
  { name: 'Green', value: '#1DD1A1' },
  { name: 'Teal', value: '#00D2D3' },
  { name: 'Blue', value: '#54A0FF' },
  { name: 'Purple', value: '#5F27CD' },
  { name: 'Pink', value: '#FF9FF3' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Slate', value: '#576574' },
]

interface CategoryFormProps {
  category?: Category // If provided, we're editing; otherwise creating
  onSuccess?: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
}

const MAX_BANNER_FILE_BYTES = 5 * 1024 * 1024

export function CategoryForm({ category, onSuccess, onCancel, mode = 'create' }: CategoryFormProps) {
  const queryClient = useQueryClient()
  const isEditing = mode === 'edit' && category
  const bannerInputRef = useRef<HTMLInputElement>(null)

  const { data: stations = [] } = useQuery({
    queryKey: ['stations'],
    queryFn: async () => {
      const res = await apiClient.getStations()
      return (res.data || []) as KitchenStation[]
    },
  })

  const stationOptions = [
    { value: 'none', label: '— Not assigned —' },
    ...stations.map((s) => ({
      value: s.id,
      label: `${s.name} (${s.output_type === 'kds' ? 'KDS' : 'Thermal printer'})`,
    })),
  ]

  // Choose the appropriate schema and default values
  const schema = isEditing ? updateCategorySchema : createCategorySchema
  const defaultValues = isEditing 
    ? {
        id: category.id,
        name: category.name,
        description: category.description || '',
        image_url: category.image_url || '',
        color: category.color || '#6B7280',
        sort_order: category.sort_order || 0,
        kitchen_station_id: category.kitchen_station_id || 'none',
        default_pct_code: category.default_pct_code || '',
      }
    : {
        name: '',
        description: '',
        image_url: '',
        color: '#6B7280',
        sort_order: 0,
        kitchen_station_id: 'none',
        default_pct_code: '',
      }

  const form = useForm<CreateCategoryData | UpdateCategoryData>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const bannerPreviewUrl = form.watch('image_url')?.trim() || ''

  // Modifier group attachment (F-MOD-N5). Held outside the RHF schema and
  // written by a side call after the category saves, the same shape as
  // kitchen_station_id above — which keeps the category create/update handler
  // untouched.
  const [modifierGroupIds, setModifierGroupIds] = useState<string[]>([])
  const [priceImpact, setPriceImpact] = useState<ModifierCategoryPriceImpact | null>(null)
  const [pendingCategoryId, setPendingCategoryId] = useState<string | null>(null)

  const { data: attachedGroupIds } = useQuery({
    queryKey: ['category-modifier-groups', category?.id],
    enabled: Boolean(category?.id),
    queryFn: async () => {
      const res = await apiClient.getCategoryModifierGroups(category!.id)
      if (!res.success) throw new Error(res.message)
      return res.data?.group_ids ?? []
    },
  })
  useEffect(() => {
    if (attachedGroupIds) setModifierGroupIds(attachedGroupIds)
  }, [attachedGroupIds])

  /**
   * Write the attachment, surfacing the server's price-impact refusal instead
   * of swallowing it.
   *
   * Returns true when the attachment is settled and false when the owner still
   * has to decide. `confirm` is only ever true on a second call made from the
   * dialog — never pre-supplied, because the second round trip IS the
   * safeguard.
   *
   * Never throws: it is called from a mutation's onSuccess, where an escaping
   * rejection would skip the cache invalidations and the success toast that
   * follow it, leaving the screen claiming nothing was saved when the category
   * itself was.
   */
  const writeCategoryModifiers = async (categoryId: string, confirm: boolean): Promise<boolean> => {
    try {
      const res = await apiClient.setCategoryModifierGroups(categoryId, modifierGroupIds, confirm)
      if (res.outcome === 'needs_confirmation') {
        setPendingCategoryId(categoryId)
        setPriceImpact(res.impact)
        return false
      }
      return true
    } catch (err) {
      toastHelpers.error(
        'Category saved, but its modifier groups were not',
        err instanceof Error ? err.message : 'Unknown error',
      )
      return false
    }
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryData) => {
      const { kitchen_station_id: _ks, ...body } = data
      return apiClient.createCategory(body)
    },
    onSuccess: async (response) => {
      const ks = form.getValues('kitchen_station_id')
      const sid = ks && ks !== 'none' ? ks : null
      const raw = response as { data?: { id?: string } }
      const newId = raw?.data?.id
      let awaitingPriceConfirm = false
      if (newId) {
        await apiClient.setCategoryKitchenStation(newId, sid)
        awaitingPriceConfirm = !(await writeCategoryModifiers(newId, false))
      }
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['stations'] })
      toastHelpers.categoryCreated(form.getValues('name') ?? '')
      // Hold the form open while the owner decides on the price impact —
      // closing it here unmounts PriceImpactDialog along with it, so the
      // decision could never be presented and the attachment could never be
      // applied. See the update path for the full note.
      if (awaitingPriceConfirm) return
      form.reset()
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Create category', error)
    },
  })

  // Update mutation  
  const updateMutation = useMutation({
    mutationFn: (data: UpdateCategoryData) => {
      const { kitchen_station_id: _ks, ...body } = data
      return apiClient.updateCategory(data.id.toString(), body)
    },
    onSuccess: async () => {
      const ks = form.getValues('kitchen_station_id')
      const sid = ks && ks !== 'none' ? ks : null
      let awaitingPriceConfirm = false
      if (category) {
        await apiClient.setCategoryKitchenStation(category.id, sid)
        awaitingPriceConfirm = !(await writeCategoryModifiers(category.id, false))
      }
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['stations'] })
      toastHelpers.apiSuccess('Update', `Category "${form.getValues('name')}"`)
      // ⭐ Do NOT close the form when the server has asked for confirmation.
      //
      // PriceImpactDialog is rendered by THIS component, so calling onSuccess
      // here unmounts it in the same commit that its state was set: the server
      // correctly refused with a 409 and wrote nothing, but the owner saw the
      // form simply close, with no explanation and no way to apply the change.
      // The safeguard protected the data and became invisible.
      if (awaitingPriceConfirm) return
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Update category', error)
    },
  })

  const onSubmit = (data: CreateCategoryData | UpdateCategoryData) => {
    if (isEditing) {
      updateMutation.mutate(data as UpdateCategoryData)
    } else {
      createMutation.mutate(data as CreateCategoryData)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {isEditing ? 'Edit Category' : 'Create New Category'}
        </CardTitle>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <TextInputField
                control={form.control}
                name="name"
                label="Category Name"
                placeholder="Enter category name"
                description="The name that will appear in the menu sections"
              />
              
              <TextareaField
                control={form.control}
                name="description"
                label="Description"
                placeholder="Describe this category..."
                rows={3}
                description="Optional description for menu organization"
              />

              <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                <div>
                  <Label className="text-base">Banner image</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Shown above this category on the online menu. Wide photos work best. Upload limit 5 MB.
                  </p>
                </div>
                <TextInputField
                  control={form.control}
                  name="image_url"
                  label="Image URL"
                  placeholder="https://example.com/image.jpg"
                  description="Optional link to a hosted image"
                />
                <div className="space-y-1.5">
                  <Label htmlFor="category-banner-file" className="text-sm font-medium">
                    Or upload an image
                  </Label>
                  <input
                    id="category-banner-file"
                    ref={bannerInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]
                      if (!f) return
                      if (f.size > MAX_BANNER_FILE_BYTES) {
                        toastHelpers.error('Image too large', 'Use a file under 5 MB, or paste an https link instead.')
                        e.target.value = ''
                        return
                      }
                      try {
                        const dataUrl = await resizeImageToDataUrl(f, 1200, 0.82)
                        form.setValue('image_url', dataUrl, { shouldValidate: true, shouldDirty: true })
                      } catch (err) {
                        toastHelpers.error('Could not process image', err instanceof Error ? err.message : String(err))
                        e.target.value = ''
                      }
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-muted-foreground"
                      onClick={() => {
                        form.setValue('image_url', '', { shouldValidate: true, shouldDirty: true })
                        if (bannerInputRef.current) bannerInputRef.current.value = ''
                      }}
                    >
                      Clear image
                    </Button>
                  </div>
                  {bannerPreviewUrl &&
                    (bannerPreviewUrl.startsWith('http') || bannerPreviewUrl.startsWith('data:image')) && (
                      <div className="pt-1">
                        <p className="mb-1 text-xs text-muted-foreground">Preview</p>
                        <img src={bannerPreviewUrl} alt="" className="h-20 w-40 rounded-md border object-cover" />
                      </div>
                    )}
                </div>
              </div>

              {/* Color Picker */}
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Color</FormLabel>
                    <FormControl>
                      <div className="space-y-3">
                        {/* Color Preview */}
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg border-2 border-border shadow-sm"
                            style={{ backgroundColor: field.value }}
                          />
                          <Input
                            {...field}
                            placeholder="#6B7280"
                            className="w-32 font-mono text-sm"
                            maxLength={7}
                          />
                        </div>
                        
                        {/* Preset Color Swatches */}
                        <div className="flex flex-wrap gap-2">
                          {PRESET_COLORS.map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => field.onChange(color.value)}
                              className="w-8 h-8 rounded-lg border-2 border-border hover:scale-110 transition-transform relative shadow-sm"
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            >
                              {field.value === color.value && (
                                <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-md" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Choose a preset color or enter a custom hex code
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Sorting */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberInputField
                control={form.control}
                name="sort_order"
                label="Sort Order"
                min={0}
                max={999}
                description="Lower numbers appear first in menus"
              />
              <SelectField
                control={form.control}
                name="kitchen_station_id"
                label="Kitchen station"
                placeholder="Choose station"
                description="KOT for items in this category goes to this station — KDS screen or thermal printer ticket."
                options={stationOptions}
              />
            </div>

            <TextInputField
              control={form.control}
              name="default_pct_code"
              label="Default FBR PCT / HS code"
              placeholder="e.g. 1905.3100 (optional)"
              description="Applied to new products in this category when their PCT is left empty. Use “Apply to products” in the categories table to bulk-update existing items."
            />

            {/* Modifier groups (F-MOD-N5). Category attachment is LIVE
                inheritance — unlike the PCT default above, which is copied
                once when a product is created. */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Questions asked on every item here</p>
              <CategoryModifierSection
                value={modifierGroupIds}
                onChange={setModifierGroupIds}
              />
            </div>

            {/* F-DEAL-N2: selling hours for the whole category; saved on its
                own button, and only for a saved category. */}
            {isEditing && category?.id != null && <CategoryHoursSection categoryId={String(category.id)} />}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <FormSubmitButton
                isLoading={isLoading}
                loadingText={isEditing ? "Updating..." : "Creating..."}
                className="flex-1"
              >
                {isEditing ? 'Update Category' : 'Create Category'}
              </FormSubmitButton>
              
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>

      {priceImpact && pendingCategoryId && (
        <PriceImpactDialog
          impact={priceImpact}
          saving={isLoading}
          onCancel={() => {
            // The category itself already saved; only the attachment is
            // unapplied. Say so rather than implying nothing happened.
            setPriceImpact(null)
            setPendingCategoryId(null)
            toastHelpers.info(
              'Modifier groups not applied',
              'The category was saved. Its modifier groups are unchanged.',
            )
            onSuccess?.()
          }}
          onConfirm={async () => {
            const ok = await writeCategoryModifiers(pendingCategoryId, true)
            if (ok) {
              setPriceImpact(null)
              setPendingCategoryId(null)
              queryClient.invalidateQueries({ queryKey: ['category-modifier-groups'] })
              queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] })
              toastHelpers.success('Modifier groups applied')
              // Now the save really is complete, so finish the way an
              // uneventful one would have.
              onSuccess?.()
            }
          }}
        />
      )}
    </Card>
  )
}
